import { type KnowledgeSection } from "./types";

/** A contiguous cluster of sections sharing one navigation group. */
export interface KnowledgeSectionGroup {
  /** The group label, or "" for ungrouped sections. */
  readonly group: string;
  readonly sections: readonly KnowledgeSection[];
}

/**
 * Cluster a view's flat section list into navigation groups for a grouped
 * sidebar / table of contents.
 *
 * Grouping is **first-seen, collect-all**: a group is keyed by the order its
 * label first appears, and every section carrying that label joins it even if
 * they are not contiguous in the raw list (the deterministic Reference layer's
 * categories are interleaved, so contiguity cannot be assumed). Sections with no
 * `group` fall into a trailing ungrouped bucket (`group: ""`), preserving order.
 */
export function groupSections(
  sections: readonly KnowledgeSection[],
): readonly KnowledgeSectionGroup[] {
  const order: string[] = [];
  const buckets = new Map<string, KnowledgeSection[]>();
  for (const section of sections) {
    const label = section.group ?? "";
    if (!buckets.has(label)) {
      buckets.set(label, []);
      order.push(label);
    }
    buckets.get(label)!.push(section);
  }
  return order.map((group) => ({ group, sections: buckets.get(group)! }));
}
